import { useState, useEffect } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Button } from '../common/Button'
import { Input } from '../common/Input'
import { useAuth0 } from '../../hooks/useAuth0'

interface ConsentPanelProps {
  callId: Id<'calls'>
  currentStep: string
  lovedOneName?: string
  lovedOnePhone?: string
  existingPhone?: string
  existingEmail?: string
}

/**
 * Notification preferences panel shown during the consent step
 * Shows loved one's phone for verification, then caregiver opt-in
 */
export function ConsentPanel({
  callId,
  currentStep,
  lovedOneName,
  lovedOnePhone,
  existingPhone,
  existingEmail,
}: ConsentPanelProps) {
  const [smsConsent, setSmsConsent] = useState(false)
  const [emailConsent, setEmailConsent] = useState(false)
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [lovedOnePhoneLocal, setLovedOnePhoneLocal] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [lovedOnePhoneError, setLovedOnePhoneError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingLovedOnePhone, setEditingLovedOnePhone] = useState(false)
  const [editingPhone, setEditingPhone] = useState(false)
  const [editingEmail, setEditingEmail] = useState(false)

  const saveConsent = useMutation(api.calls.saveConsentFromUI)
  const { loginWithRedirect } = useAuth0()

  // Format phone for display
  const formatPhoneDisplay = (phoneNum: string): string => {
    const digits = phoneNum.replace(/\D/g, '')
    const localDigits = digits.startsWith('1') ? digits.slice(1) : digits
    if (localDigits.length === 10) {
      return `(${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`
    }
    return phoneNum
  }

  // Update local state when props change
  useEffect(() => {
    if (existingPhone) {
      setPhone(formatPhoneDisplay(existingPhone))
    }
  }, [existingPhone])

  useEffect(() => {
    if (existingEmail) {
      setEmail(existingEmail)
    }
  }, [existingEmail])

  useEffect(() => {
    if (lovedOnePhone) {
      setLovedOnePhoneLocal(formatPhoneDisplay(lovedOnePhone))
    }
  }, [lovedOnePhone])

  // Show during consent step and finalization (since we initiated the finalization
  // transition via saveConsentFromUI and need to stay mounted for Auth0 redirect)
  if (currentStep !== 'consent' && currentStep !== 'finalization') {
    return null
  }

  const hasPhone = !!phone.trim()
  const hasEmail = !!email.trim()
  const hasLovedOnePhone = !!lovedOnePhoneLocal.trim()

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').substring(0, 10)
    if (digits.length === 0) return ''
    if (digits.length <= 3) return `(${digits}`
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhone(formatted)
    if (phoneError) setPhoneError('')
  }

  const handleLovedOnePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setLovedOnePhoneLocal(formatted)
    if (lovedOnePhoneError) setLovedOnePhoneError('')
  }

  const handleSubmit = async () => {
    // Validate loved one's phone
    const lovedOneDigits = lovedOnePhoneLocal.replace(/\D/g, '')
    if (lovedOneDigits.length !== 10) {
      setLovedOnePhoneError('Please enter a valid 10-digit phone number')
      return
    }

    // Validate caregiver phone if SMS consent
    if (smsConsent) {
      const digits = phone.replace(/\D/g, '')
      if (digits.length !== 10) {
        setPhoneError('Please enter a valid 10-digit phone number')
        return
      }
    }

    // Validate email if email consent
    if (emailConsent) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        setEmailError('Please enter a valid email address')
        return
      }
    }

    setIsSubmitting(true)
    try {
      const formattedPhone = smsConsent && phone ? `+1${phone.replace(/\D/g, '')}` : undefined
      const formattedLovedOnePhone = `+1${lovedOneDigits}`

      // Store callId in localStorage BEFORE the mutation, so even if Convex
      // reactive updates cause a re-render that unmounts this component,
      // the OAuthCallback will still have the callId to complete onboarding.
      localStorage.setItem('voiceOnboardingCallId', callId)

      await saveConsent({
        callId,
        smsConsent,
        emailConsent,
        phone: formattedPhone,
        email: emailConsent ? email.trim() : undefined,
        lovedOnePhone: formattedLovedOnePhone,
      })

      // Redirect to Auth0 signup
      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
        },
        appState: {
          returnTo: '/dashboard',
        },
      })
    } catch (error) {
      console.error('Failed to save consent:', error)
      // Clean up localStorage on failure so we don't leave stale state
      localStorage.removeItem('voiceOnboardingCallId')
      setIsSubmitting(false)
    }
  }

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <span style={headerIconStyles}>🔔</span>
        <span>Notification Preferences</span>
      </div>

      <div style={contentStyles}>
        {/* Loved One's Phone Section */}
        <div style={sectionStyles}>
          <div style={sectionTitleStyles}>We'll call {lovedOneName || 'your loved one'} at:</div>
          <div style={contactDisplayStyles}>
            <span style={contactIconStyles}>📞</span>
            {editingLovedOnePhone ? (
              <div style={editInputContainerStyles}>
                <Input
                  label=""
                  placeholder="(555) 123-4567"
                  type="tel"
                  value={lovedOnePhoneLocal}
                  onChange={handleLovedOnePhoneChange}
                  error={lovedOnePhoneError}
                  prefix="+1"
                />
                <button style={doneButtonStyles} onClick={() => setEditingLovedOnePhone(false)}>
                  Done
                </button>
              </div>
            ) : (
              <>
                <span style={contactValueStyles}>
                  {hasLovedOnePhone ? lovedOnePhoneLocal : 'Not provided'}
                </span>
                <button style={editButtonStyles} onClick={() => setEditingLovedOnePhone(true)}>
                  Edit
                </button>
              </>
            )}
          </div>
          {lovedOnePhoneError && !editingLovedOnePhone && (
            <div style={errorTextStyles}>{lovedOnePhoneError}</div>
          )}
        </div>

        <div style={dividerStyles} />

        {/* Caregiver Notification Section */}
        <div style={sectionStyles}>
          <div style={sectionTitleStyles}>How should we update YOU?</div>

          {/* SMS Option */}
          <div style={optionContainerStyles}>
            <div style={contactRowStyles}>
              <span style={contactIconStyles}>📱</span>
              <span style={optionLabelStyles}>Your Phone:</span>
              {editingPhone ? (
                <div style={editInputContainerStyles}>
                  <Input
                    label=""
                    placeholder="(555) 123-4567"
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    error={phoneError}
                    prefix="+1"
                  />
                  <button style={doneButtonStyles} onClick={() => setEditingPhone(false)}>
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <span style={contactValueStyles}>{hasPhone ? phone : 'Not provided'}</span>
                  <button style={editButtonStyles} onClick={() => setEditingPhone(true)}>
                    Edit
                  </button>
                </>
              )}
            </div>
            <label style={consentLabelStyles}>
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={e => setSmsConsent(e.target.checked)}
                style={checkboxStyles}
              />
              <span style={consentTextStyles}>
                <strong>Yes, text me</strong> updates about check-ins, medication reminders, and
                important alerts.
              </span>
            </label>
            <div style={legalMicroStyles}>Msg & data rates may apply. Reply STOP to opt out.</div>
            {phoneError && !editingPhone && <div style={errorTextStyles}>{phoneError}</div>}
          </div>

          {/* Email Option */}
          <div style={optionContainerStyles}>
            <div style={contactRowStyles}>
              <span style={contactIconStyles}>✉️</span>
              <span style={optionLabelStyles}>Your Email:</span>
              {editingEmail ? (
                <div style={editInputContainerStyles}>
                  <Input
                    label=""
                    placeholder="you@example.com"
                    type="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value)
                      if (emailError) setEmailError('')
                    }}
                    error={emailError}
                  />
                  <button style={doneButtonStyles} onClick={() => setEditingEmail(false)}>
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <span style={contactValueStyles}>{hasEmail ? email : 'Not provided'}</span>
                  <button style={editButtonStyles} onClick={() => setEditingEmail(true)}>
                    Edit
                  </button>
                </>
              )}
            </div>
            <label style={consentLabelStyles}>
              <input
                type="checkbox"
                checked={emailConsent}
                onChange={e => setEmailConsent(e.target.checked)}
                style={checkboxStyles}
              />
              <span style={consentTextStyles}>
                <strong>Yes, email me</strong> care summaries and important updates.
              </span>
            </label>
            {emailError && !editingEmail && <div style={errorTextStyles}>{emailError}</div>}
          </div>
        </div>

        <div style={dividerStyles} />

        {/* Legal Confirmation */}
        <div style={legalTextStyles}>
          By clicking Save, I confirm the information above is correct and consent to receive the
          communications I selected from Little Sister.
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || (!smsConsent && !emailConsent)}
          style={{ width: '100%', marginTop: '16px' }}
        >
          {isSubmitting ? 'Saving...' : 'Save & Create Account'}
        </Button>

        {!smsConsent && !emailConsent && (
          <p style={hintTextStyles}>Select at least one notification method</p>
        )}
      </div>
    </div>
  )
}

// Styles
const containerStyles: React.CSSProperties = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '2px solid #3b82f6',
  overflow: 'hidden',
}

const headerStyles: React.CSSProperties = {
  padding: '16px 20px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  fontSize: '18px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}

const headerIconStyles: React.CSSProperties = {
  fontSize: '20px',
}

const contentStyles: React.CSSProperties = {
  padding: '20px',
}

const sectionStyles: React.CSSProperties = {
  marginBottom: '8px',
}

const sectionTitleStyles: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '12px',
}

const contactDisplayStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 16px',
  backgroundColor: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: '8px',
}

const contactRowStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '10px',
  flexWrap: 'wrap',
}

const contactIconStyles: React.CSSProperties = {
  fontSize: '18px',
  flexShrink: 0,
}

const contactValueStyles: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#1f2937',
  flex: 1,
}

const optionLabelStyles: React.CSSProperties = {
  fontSize: '14px',
  color: '#6b7280',
  fontWeight: 500,
}

const editButtonStyles: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#3b82f6',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
}

const editInputContainerStyles: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
}

const doneButtonStyles: React.CSSProperties = {
  background: '#3b82f6',
  border: 'none',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: '8px 12px',
  borderRadius: '6px',
  marginTop: '4px',
}

const dividerStyles: React.CSSProperties = {
  height: '1px',
  backgroundColor: '#e5e7eb',
  margin: '16px 0',
}

const optionContainerStyles: React.CSSProperties = {
  padding: '14px 16px',
  backgroundColor: '#fafafa',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  marginBottom: '12px',
}

const consentLabelStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  cursor: 'pointer',
}

const checkboxStyles: React.CSSProperties = {
  width: '20px',
  height: '20px',
  marginTop: '2px',
  cursor: 'pointer',
  accentColor: '#3b82f6',
}

const consentTextStyles: React.CSSProperties = {
  fontSize: '14px',
  color: '#374151',
  lineHeight: 1.5,
}

const legalMicroStyles: React.CSSProperties = {
  fontSize: '11px',
  color: '#9ca3af',
  marginTop: '6px',
  marginLeft: '30px',
}

const legalTextStyles: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  lineHeight: 1.6,
  padding: '12px',
  backgroundColor: '#f9fafb',
  borderRadius: '6px',
  textAlign: 'center',
}

const errorTextStyles: React.CSSProperties = {
  fontSize: '13px',
  color: '#dc2626',
  marginTop: '8px',
}

const hintTextStyles: React.CSSProperties = {
  fontSize: '13px',
  color: '#6b7280',
  textAlign: 'center',
  marginTop: '12px',
  marginBottom: 0,
}
